import React, { useState, useEffect, useRef } from 'react'
import {
  DndContext,
  closestCenter,
  DragStartEvent,
  DragEndEvent,
  DragOverlay,
  DragOverEvent,
  PointerSensor,
  useDndMonitor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { Drag } from 'components/icons'
import { tv } from 'tailwind-variants'
import Tooltip from 'components/tooltip'
import { MenuFooter } from 'components/menu/items'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { IColumnTypes } from 'components/spreadsheet/types'
import { ITableViewColumn } from 'types'
import { getOriginalViewColumns } from 'components/spreadsheet/helpers/functions'
import { computeInsertIndex, moveColumnBlock } from 'components/spreadsheet/helpers/columnOrder'
import { EMPTY_SELECTION, joinOf, nextSelection, SelectionState } from 'components/sidebar/components/folders/selection'
import { SelectionJoin } from 'components/sidebar/surface'

// Increased stack offset to prevent overlapping
const STACK_OFFSET = 12

// A column's row, set as the menus set theirs: 32px, 13px medium, a faint handle, the column's type
// icon and its name. Picked rows (to drag together) take a light tint, and a run of them draws as one
// block, as in the sidebar: each squares the corners it shares with a picked neighbour.
const orderRow = tv({
  base: 'flex h-8 select-none items-center gap-2 rounded-md px-2 text-[13px] font-medium text-body',
  variants: {
    selected: {
      true: 'bg-blue/10',
      false: 'hover:bg-surface-hover'
    },
    join: {
      none: '',
      above: 'rounded-t-none',
      below: 'rounded-b-none',
      both: 'rounded-none'
    }
  }
})

// ⌘ on a Mac, Ctrl elsewhere; either one works everywhere.
const TOGGLE_KEY = typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgent) ? '⌘' : 'Ctrl'

const RowFace: React.FC<{ name: string; kind: IColumnTypes }> = ({ name, kind }) => (
  <>
    <Drag className="size-3.5 shrink-0 text-faint" />
    <IconSelector kind={kind} />
    <Tooltip content={name} showOnlyIfTruncated>
      <span className="min-w-0 flex-1 truncate">{name}</span>
    </Tooltip>
  </>
)

function useRafState<S>(initial: S): [S, (v: S) => void] {
  const [state, setState] = useState<S>(initial)
  const raf = useRef<number | undefined>(undefined)
  const queued = useRef<S>(state)

  const set = (v: S) => {
    queued.current = v
    if (raf.current === undefined) {
      raf.current = requestAnimationFrame(() => {
        raf.current = undefined
        setState(queued.current)
      })
    }
  }

  useEffect(() => {
    return () => {
      if (raf.current !== undefined) cancelAnimationFrame(raf.current)
    }
  }, [])

  return [state, set]
}

/**
 * The view's whole column list in a new order. The reorder replaces the view's list, so every column
 * the menu doesn't list (a hidden one) stays in its slot, and the listed ones fill the others in
 * `newOrder`'s order. The pivot column is left out, for the caller to put back at the front.
 */
export const mergeColumnOrder = (
  sourceColumns: ITableViewColumn[],
  newOrder: string[],
  pivotColumnId: string | null
): ITableViewColumn[] => {
  const listed = new Set(newOrder)
  const byId = new Map(sourceColumns.map((c) => [c.publicId, c]))
  let next = 0
  return sourceColumns
    .filter((c) => c.publicId !== pivotColumnId)
    .map((c) => (listed.has(c.publicId) ? byId.get(newOrder[next++]) : c))
    .filter((c): c is ITableViewColumn => c !== undefined)
}

type ColumnOrderMenuProps = MenuProps

const ColumnOrderMenu: React.FC<ColumnOrderMenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()

  // For transformed views, operate on original columns (which appear as rows), not synthetic columns
  const sourceColumns = getOriginalViewColumns(spreadsheetData)

  // Columns that are not hidden (different from columns in viewport).
  // In transformed views, also exclude the pivot column since it's always the first row.
  const pivotColumnId = spreadsheetData.viewDetails.transposeSettings?.pivotColumnId ?? null
  const visibleColumns = sourceColumns.filter(
    (c) => !spreadsheetData.userConfiguration.hiddenColumns.includes(c.publicId) && c.publicId !== pivotColumnId
  )

  const [order, setOrder] = useState<string[]>(visibleColumns.map((c) => c.publicId))
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION)
  const selected = selection.selected
  const [draggedIds, setDraggedIds] = useState<string[]>([])
  const [placeholderIdx, setPlaceholderIdx] = useRafState<number | null>(null)

  // What a drag carries: the picked columns when it starts on one of them, in the order they stand.
  const groupOf = (id: string) => (selected.includes(id) ? order.filter((pid) => selected.includes(pid)) : [id])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6 // start dragging only after the cursor moves 6 px
      }
    })
  )

  // Starts again from the view's order when its columns change, and only then: keyed on the ids, as
  // the filtered list is a new array on every render.
  const columnKey = visibleColumns.map((c) => c.publicId).join(',')
  useEffect(() => {
    setOrder(columnKey ? columnKey.split(',') : [])
  }, [columnKey])

  const commitOrder = (newOrder: string[]) => {
    const reordered = mergeColumnOrder(sourceColumns, newOrder, pivotColumnId)

    // The pivot column is excluded from the menu UI but still belongs in viewDetails.columns.
    // Restore it at the front so the transpose view can resolve its name and save correctly.
    if (pivotColumnId) {
      const pivotCol = sourceColumns.find((c) => c.publicId === pivotColumnId)
      if (pivotCol) reordered.unshift(pivotCol)
    }

    const withSortOrder = reordered.map((c, idx) => ({ ...c, sortOrder: idx }))
    setSpreadsheetData({ type: 'CHANGE_ALL_COLUMNS_ORDER', columns: withSortOrder })
  }

  // As in the sidebar: ⌘/Ctrl-click adds or takes out one column, Shift-click picks every column from
  // the last one clicked, and a plain click picks just that one.
  const pick = (id: string, { toggle, range }: { toggle: boolean; range: boolean }) =>
    setSelection((prev) =>
      toggle || range ? nextSelection(prev, { id, toggle, range }, order) : { selected: [id], anchor: id }
    )

  const onDragStart = (e: DragStartEvent) => setDraggedIds(groupOf(e.active.id as string))

  const onDragOver = (e: DragOverEvent) => {
    if (!e.over) {
      if (placeholderIdx !== null) setPlaceholderIdx(null)
      return
    }

    const grp = groupOf(e.active.id as string)
    const overId = e.over.id as string

    // If hovering over a dragged item, clear placeholder
    if (grp.includes(overId)) {
      if (placeholderIdx !== null) setPlaceholderIdx(null)
      return
    }

    // Get the order without the dragged group
    const { insertIndex } = computeInsertIndex(order, grp, overId)
    if (insertIndex !== placeholderIdx) setPlaceholderIdx(insertIndex)
  }

  const onDragEnd = (e: DragEndEvent) => {
    setDraggedIds([])
    setPlaceholderIdx(null)
    if (!e.over) return

    const grp = groupOf(e.active.id as string)
    const overId = e.over.id as string
    if (grp.includes(overId)) return

    const newOrder = moveColumnBlock(order, grp, overId)

    setOrder(newOrder)
    commitOrder(newOrder)
  }

  // Get the filtered order for rendering (excluding dragged items)
  const filteredOrderLength = order.filter((id) => !draggedIds.includes(id)).length

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={width}
      zIndex={3000}
      title="Column order"
    >
      <div className="max-h-[360px] overflow-y-auto overscroll-contain p-1">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOver={onDragOver}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            {order.map((pid, idx) => {
              const col = visibleColumns.find((c) => c.publicId === pid)
              if (!col) return null
              const isDragged = draggedIds.includes(pid)
              const visibleIdx = order.filter((id, i) => i <= idx && !draggedIds.includes(id)).length - 1

              return (
                <div key={pid}>
                  {placeholderIdx === visibleIdx && !isDragged && <DropIndicator />}
                  <SortableRow
                    id={pid}
                    name={col.name}
                    kind={col.kind}
                    selected={selected.includes(pid)}
                    join={joinOf(order, pid, (id) => selected.includes(id))}
                    hiddenWhileDrag={isDragged}
                    pick={pick}
                  />
                </div>
              )
            })}
            {placeholderIdx === filteredOrderLength && <DropIndicator />}
          </SortableContext>
          <DragOverlay>
            {draggedIds.length > 0 && (
              <div className="relative w-full pointer-events-none">
                {draggedIds.map((id, i) => {
                  const col = visibleColumns.find((c) => c.publicId === id)
                  if (!col) return null
                  return (
                    <RowStackFace
                      key={id}
                      name={col.name}
                      kind={col.kind}
                      index={i}
                      isTop={i === 0}
                      stackOffset={STACK_OFFSET}
                    />
                  )
                })}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>
      <MenuFooter>Drag to reorder. {TOGGLE_KEY}- or Shift-click to pick several.</MenuFooter>
    </FloatingMenu>
  )
}

const DropIndicator: React.FC = () => (
  <div className="relative pointer-events-none h-0 z-[2000]">
    <div className="absolute inset-x-2 top-[-1px] h-0.5 rounded-full bg-blue" />
  </div>
)

interface SortableRowProps {
  id: string
  name: string
  kind: IColumnTypes
  selected: boolean
  join: SelectionJoin
  hiddenWhileDrag: boolean
  pick: (id: string, click: { toggle: boolean; range: boolean }) => void
}

const SortableRow: React.FC<SortableRowProps> = ({ id, name, kind, selected, join, hiddenWhileDrag, pick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  // A press on a picked column may start dragging all of them, so it only narrows the pick to this
  // one on release, if it didn't turn into a drag.
  const narrowOnRelease = useRef(false)
  useDndMonitor({ onDragStart: () => (narrowOnRelease.current = false) })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || hiddenWhileDrag ? 0 : 1,
    cursor: selected ? 'grab' : 'pointer',
    visibility: hiddenWhileDrag ? 'hidden' : 'visible'
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (hiddenWhileDrag) return // Don't allow interaction with hidden items

    const toggle = e.metaKey || e.ctrlKey
    if (toggle || e.shiftKey) {
      pick(id, { toggle, range: e.shiftKey })
      e.preventDefault()
      e.stopPropagation()
      return
    }
    narrowOnRelease.current = selected
    if (!selected) pick(id, { toggle: false, range: false })
    ;(listeners as any).onPointerDown?.(e)
  }

  const onClick = () => {
    if (narrowOnRelease.current) pick(id, { toggle: false, range: false })
    narrowOnRelease.current = false
  }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onPointerDown={onPointerDown}
      onClick={onClick}
      // Ctrl-click on a Mac is a right-click: it picks a column here, so no browser menu.
      onContextMenu={(e) => e.ctrlKey && e.preventDefault()}
      className={orderRow({ selected, join })}
      style={style}
    >
      <RowFace name={name} kind={kind} />
    </div>
  )
}

export default ColumnOrderMenu

interface RowStackFaceProps {
  name: string
  kind: IColumnTypes
  index: number // 0…n-1 inside the group
  isTop: boolean
  stackOffset: number // pixels per level
}

const RowStackFace: React.FC<RowStackFaceProps> = ({ name, kind, index, isTop, stackOffset }) => (
  <div
    className={orderRow({
      selected: true,
      class: 'absolute inset-x-0 top-0 bg-surface-raised shadow-[0_4px_12px_-2px_rgb(0_0_0/0.16)] ring-1 ring-divider'
    })}
    style={{
      transform: `translateY(${index * stackOffset}px)`,
      opacity: isTop ? 1 : 0.6, // Add z-index to ensure proper stacking
      zIndex: 1000 - index
    }}
  >
    {isTop && <RowFace name={name} kind={kind} />}
  </div>
)
